import React from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import CardBase from "../../components/global/CardBase";
import dayjs from "dayjs";
import SelectBase, { SelectOption } from "../../components/global/SelectBase";
import DatePickerBase from "../../components/global/DatePickerBase";
import { Controller, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import { useGetProjectSummaryQuery } from "../../services/report.service";
import { formatMoney } from "../../utils/helper";

const periodOptions: SelectOption[] = [
  { value: "this-week", label: "This Week" },
  { value: "this-month", label: "This Month" },
  { value: "this-year", label: "This Year" },
  { value: "other", label: "Custom Dates" },
];

const ProjectSummaryCard = () => {
  const [period, setPeriod] = React.useState<SelectOption | null>(
    periodOptions.find((opt) => opt.value === "this-year") || null
  );
  const [periodDate, setPeriodDate] = React.useState({
    startDate: dayjs().startOf("year").format("YYYY-MM-DD"),
    endDate: dayjs().endOf("year").format("YYYY-MM-DD"),
  });

  const { data, isLoading, isFetching } = useGetProjectSummaryQuery({
    startDate: periodDate.startDate,
    endDate: periodDate.endDate,
  });

  const totalPay = React.useMemo(
    () =>
      data?.data
        ?.map((item) => Number(item?.total_pay || 0))
        .reduce((prev, curr) => prev + curr, 0) || 0,
    [data]
  );
  const totalFee = React.useMemo(
    () =>
      data?.data
        ?.map((item) => Number(item?.total_fee || 0))
        .reduce((prev, curr) => prev + curr, 0) || 0,
    [data]
  );
  const totalCompleted = React.useMemo(
    () =>
      data?.data
        ?.map((item) => Number(item?.number_of_completed || 0))
        .reduce((prev, curr) => prev + curr, 0) || 0,
    [data]
  );

  const {
    handleSubmit,
    control,
    clearErrors,
    setValue,
    setError,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!dayjs(payload.start_date).isSame(dayjs(payload.end_date))) {
        if (dayjs(payload.start_date).isAfter(dayjs(payload.end_date))) {
          setError("end_date", { type: "custom", message: "Invalid" });
          return;
        }
      }
      setPeriodDate({
        startDate: dayjs(payload.start_date).format("YYYY-MM-DD"),
        endDate: dayjs(payload.end_date).format("YYYY-MM-DD"),
      });
    } catch (err: any) {
      toast.error(err);
    }
  };

  React.useEffect(() => {
    setValue("start_date", dayjs().add(-1, "day"));
    setValue("end_date", dayjs());
    clearErrors();
  }, [period]);

  return (
    <CardBase
      title="Summary"
      sx={{ height: "100%" }}
      useGlassCard={false}
      headerActions={
        <Box sx={{ minWidth: 120, mt: 0 }}>
          <form key="talent-company-stat" onSubmit={handleSubmit(onSubmit)}>
            <Stack direction="row" spacing={1}>
              <SelectBase
                options={periodOptions}
                value={period}
                placeholder="Period"
                onChange={(selectedValue) => {
                  const option = selectedValue as SelectOption | null;
                  if (!option) return;

                  const value = option.value as string;
                  if (value === "this-week") {
                    setPeriodDate({
                      startDate: dayjs().startOf("week").format("YYYY-MM-DD"),
                      endDate: dayjs().endOf("week").format("YYYY-MM-DD"),
                    });
                  } else if (value === "this-month") {
                    setPeriodDate({
                      startDate: dayjs()
                        .startOf("month")
                        .format("YYYY-MM-DD"),
                      endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
                    });
                  } else if (value === "this-year") {
                    setPeriodDate({
                      startDate: dayjs().startOf("year").format("YYYY-MM-DD"),
                      endDate: dayjs().endOf("year").format("YYYY-MM-DD"),
                    });
                  }
                  setPeriod(option);
                }}
                sx={{ minWidth: 150, mt: 0 }}
              />
            </Stack>
            {period?.value === "other" && (
              <Stack>
                <Controller
                  name="start_date"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <DatePickerBase
                      label="Start Date"
                      value={value ? dayjs(value) : null}
                      onChange={(newValue) => onChange(newValue)}
                      error={!!errors.start_date}
                      helperText={
                        !!errors.start_date && "This field is required"
                      }
                      sx={{ mt: 1 }}
                      required
                    />
                  )}
                />
                <Controller
                  name="end_date"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field: { onChange, value } }) => (
                    <DatePickerBase
                      label="End Date"
                      value={value ? dayjs(value) : null}
                      onChange={(newValue) => onChange(newValue)}
                      error={!!errors.end_date}
                      helperText={
                        !!errors.end_date &&
                        "The end date must be a date after or equal to start date"
                      }
                      sx={{ mt: 1 }}
                      required
                    />
                  )}
                />
                <LoadingButton
                  type="submit"
                  variant="contained"
                  sx={{ mt: 1, height: "100%" }}
                >
                  Update
                </LoadingButton>
              </Stack>
            )}
          </form>
        </Box>
      }
    >
      <Box sx={{ mt: 2 }}>
        {isLoading || isFetching ? (
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <CardBase
                useGlassCard={true}
                sx={{
                  textAlign: "center",
                  p: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    color: "green",
                    fontWeight: "bold",
                  }}
                >
                  ${formatMoney(totalPay || 0)}
                </Typography>
                <Typography variant="caption">Total Pay</Typography>
              </CardBase>
            </Grid>
            <Grid item xs={6}>
              <CardBase
                useGlassCard={true}
                sx={{
                  textAlign: "center",
                  p: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    color: "green",
                    fontWeight: "bold",
                  }}
                >
                  ${formatMoney(totalFee || 0)}
                </Typography>
                <Typography variant="caption">Total Fee</Typography>
              </CardBase>
            </Grid>
            <Grid item xs={6}>
              <CardBase
                useGlassCard={true}
                sx={{
                  textAlign: "center",
                  p: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    color: "green",
                    fontWeight: "bold",
                  }}
                >
                  {totalCompleted || 0}
                </Typography>
                <Typography variant="caption">Number of Ticket</Typography>
              </CardBase>
            </Grid>
            <Grid item xs={6}>
              <CardBase
                useGlassCard={true}
                sx={{
                  textAlign: "center",
                  p: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    color: "green",
                    fontWeight: "bold",
                  }}
                >
                  ${formatMoney((totalPay || 0) / (totalCompleted || 0))}
                </Typography>
                <Typography variant="caption">
                  Average Pay Per Ticket
                </Typography>
              </CardBase>
            </Grid>
          </Grid>
        )}
      </Box>
    </CardBase>
  );
};
export default ProjectSummaryCard;
